import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { cellSx, headerSx, fieldSx, ManifestEntry } from './PartsShipmentsHelpers';

export interface ManifestSectionProps {
  manifest: ManifestEntry[];
  serialised: ManifestEntry[];
  nonSerialised: ManifestEntry[];
  partSearchInput: string;
  partOptions: Array<Record<string, unknown>>;
  partSearchPending: boolean;
  onPartSearchInputChange: (val: string) => void;
  onAddItem: (item: Record<string, unknown>) => void;
  onRemoveItem: (key: string) => void;
  onUpdateQty: (key: string, qty: number) => void;
  onExport: () => void;
}

export const ManifestSection: React.FC<ManifestSectionProps> = ({
  manifest, serialised, nonSerialised,
  partSearchInput, partOptions, partSearchPending,
  onPartSearchInputChange, onAddItem, onRemoveItem, onUpdateQty, onExport,
}) => (
  <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', mt: 1.5, pt: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Manifest</Typography>
      {manifest.length > 0 && (
        <Button size="small" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={onExport} sx={{ fontSize: '10px', textTransform: 'none', color: 'v4.accent' }}>
          Export
        </Button>
      )}
    </Box>

    <Autocomplete size="small" options={partOptions}
      getOptionLabel={(opt) => {
        const o = opt as Record<string, unknown>;
        const pt = o.partType as Record<string, unknown> | undefined;
        const name = String(pt?.name ?? o.name ?? '');
        return o.serial1 ? `${name} — ${String(o.serial1)}` : name;
      }}
      filterOptions={(x) => x}
      inputValue={partSearchInput}
      onInputChange={(_, val, reason) => { if (reason !== 'reset') onPartSearchInputChange(val); }}
      onChange={(_, val) => { if (val) onAddItem(val as Record<string, unknown>); }}
      value={null}
      blurOnSelect
      clearOnBlur={false}
      loading={partSearchPending}
      renderInput={(params) => (
        <MuiTextField {...params} placeholder="Search by serial, part type name or number..."
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '12px' } }} />
      )}
      renderOption={(props, option) => {
        const o = option as Record<string, unknown>;
        const pt = o.partType as Record<string, unknown> | undefined;
        const pc = o.partCondition as Record<string, unknown> | undefined;
        return (
          <li {...props} key={`${o.id}-${o.serial1 ?? Math.random()}`}>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                {String(pt?.name ?? o.name ?? '-')}{o.serial1 ? ` — ${String(o.serial1)}` : ''}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                {String(pt?.number ?? '')}{pc?.name ? ` · ${String(pc.name)}` : ''}{o.locationName ? ` · ${String(o.locationName)}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
    />

    {serialised.length > 0 && (
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
          Serialised ({serialised.length})
        </Typography>
        <TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell sx={headerSx}>Part Type</TableCell>
            <TableCell sx={headerSx}>Part No</TableCell>
            <TableCell sx={headerSx}>Serial</TableCell>
            <TableCell sx={{ ...headerSx, width: 40 }} />
          </TableRow></TableHead>
          <TableBody>{serialised.map((m) => (
            <TableRow key={m.key} sx={{ '&:hover': { bgcolor: 'v4.tableHover' } }}>
              <TableCell sx={cellSx}>{m.partTypeName}</TableCell>
              <TableCell sx={cellSx}>{m.partTypeNo}</TableCell>
              <TableCell sx={cellSx}>{m.serial}</TableCell>
              <TableCell sx={cellSx}>
                <IconButton size="small" onClick={() => onRemoveItem(m.key)} aria-label="Remove item">
                  <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      </Box>
    )}

    {nonSerialised.length > 0 && (
      <Box>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
          Non-Serialised ({nonSerialised.length})
        </Typography>
        <TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell sx={headerSx}>Part Type</TableCell>
            <TableCell sx={headerSx}>Part No</TableCell>
            <TableCell sx={{ ...headerSx, width: 60 }}>Qty</TableCell>
            <TableCell sx={{ ...headerSx, width: 40 }} />
          </TableRow></TableHead>
          <TableBody>{nonSerialised.map((m) => (
            <TableRow key={m.key} sx={{ '&:hover': { bgcolor: 'v4.tableHover' } }}>
              <TableCell sx={cellSx}>{m.partTypeName}</TableCell>
              <TableCell sx={cellSx}>{m.partTypeNo}</TableCell>
              <TableCell sx={cellSx}>
                <InputBase type="number" value={m.quantity}
                  onChange={(e) => onUpdateQty(m.key, parseInt(e.target.value) || 1)}
                  sx={{ ...fieldSx, width: 50, px: 0.5 }} inputProps={{ min: 1 }} />
              </TableCell>
              <TableCell sx={cellSx}>
                <IconButton size="small" onClick={() => onRemoveItem(m.key)} aria-label="Remove item">
                  <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      </Box>
    )}

    {manifest.length === 0 && (
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', textAlign: 'center', py: 1 }}>
        Search above to add parts to the manifest
      </Typography>
    )}
  </Box>
);
